import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Steps,
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  Checkbox,
  Spin,
  message,
  App,
  Divider,
  Tag,
  Row,
  Col,
  Descriptions,
  Tabs,
  Avatar,
  Badge,
  Statistic,
  Empty,
  Tooltip,
  Modal,
  Alert,
  List,
  Switch,
  Radio,
  Popconfirm
} from 'antd';
import {
  FileTextOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  ApartmentOutlined,
  EditOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FolderOutlined,
  BankOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  CloseOutlined,
  UserAddOutlined,
  FilterOutlined,
  SaveOutlined,
  DeleteOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from './PageHeader';
import CsvPeopleImport from './CsvPeopleImport';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import campaignService from '../services/campaignService';
import questionnaireService from '../services/questionnaireService';
import smsService from '../services/smsService';
import settingsService from '../services/settingsService';

const { Step } = Steps;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const CampaignForm = () => {
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  
  // Data states
  const [questionnaires, setQuestionnaires] = useState([]);
  const [questionnaireOptionsLoading, setQuestionnaireOptionsLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [advTargetVisible, setAdvTargetVisible] = useState(false);
  const [advTargetKeywords, setAdvTargetKeywords] = useState([]); // confirmed keyword tags
  const [advTargetInput, setAdvTargetInput] = useState('');       // text currently being typed
  const [advTargetMatchMode, setAdvTargetMatchMode] = useState('word'); // 'word' | 'exact' | 'contains'
  const [advTargetGroups, setAdvTargetGroups] = useState([]);
  const [advTargetExcludedGroups, setAdvTargetExcludedGroups] = useState(new Set());
  const [advTargetBUs, setAdvTargetBUs] = useState([]);           // selected business unit titles
  const [advTargetPreviewExpanded, setAdvTargetPreviewExpanded] = useState(false);
  const [advTargetIncludedDepts, setAdvTargetIncludedDepts] = useState(new Set());
  const [advSavedFilters, setAdvSavedFilters] = useState([]);
  const [advSaveFilterName, setAdvSaveFilterName] = useState('');

  // Panel targeting state
  const [panelActiveGroup, setPanelActiveGroup] = useState(null);
  const [panelActiveBU, setPanelActiveBU] = useState(null);
  const [panelSearch, setPanelSearch] = useState('');
  const [savedSelections, setSavedSelections] = useState([]);
  const [savedSelectionName, setSavedSelectionName] = useState('');

  // Auto-select managing groups based on selected BUs and/or active keywords
  // Also auto-select all BUs within keyword-matched groups
  useEffect(() => {
    const allKws = [
      ...advTargetKeywords,
      ...(advTargetInput.trim() ? [advTargetInput.trim()] : [])
    ].map(k => k.toLowerCase());

    const matched = new Set();
    const autoSelectedBUs = [];

    (organizations || []).forEach(group => {
      // Group is implied by a keyword matching a dept inside it
      if (allKws.length) {
        const hasDept = (group.children || []).some(bu =>
          (bu.children || []).some(dept => {
            const deptLower = dept.title.toLowerCase();
            return allKws.some(kw => {
              if (advTargetMatchMode === 'exact') return deptLower === kw;
              if (advTargetMatchMode === 'contains') return deptLower.includes(kw);
              // word match
              const regex = new RegExp(`(^|\\s|-|[&/])${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|\\s|-|[&/])`, 'i');
              return regex.test(deptLower);
            });
          })
        );
        if (hasDept) {
          matched.add(group.title);
          // Auto-select all BUs in this keyword-matched group
          (group.children || []).forEach(bu => autoSelectedBUs.push(bu.title));
        }
      }
    });

    // Also mark groups that contain a manually-selected BU
    (organizations || []).forEach(group => {
      if (!matched.has(group.title)) {
        const hasBU = (group.children || []).some(bu => advTargetBUs.includes(bu.title));
        if (hasBU) matched.add(group.title);
      }
    });

    setAdvTargetGroups([...matched]);
    // Sync BUs from keyword-matched groups (don't remove manually selected BUs)
    if (autoSelectedBUs.length > 0) {
      setAdvTargetBUs(prev => {
        const merged = Array.from(new Set([...prev, ...autoSelectedBUs]));
        // Only update if actually changed to avoid loops
        if (merged.length === prev.length && merged.every(b => prev.includes(b))) return prev;
        return merged;
      });
    }
  }, [advTargetKeywords, advTargetInput, advTargetBUs, organizations, advTargetMatchMode]);
  const [selectedQuestionnaireDetails, setSelectedQuestionnaireDetails] = useState(null);
  const [reuseModalState, setReuseModalState] = useState({
    open: false,
    summary: [],
    questionnaire: null,
    questionnaireId: null,
    previousQuestionnaireId: null
  });
  
  // People stage states
  const [searchQuery, setSearchQuery] = useState('');
  const [actualSearchQuery, setActualSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedPeople, setSelectedPeople] = useState([]);
  
  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    questionnaire: null,
    startDate: null,
    endDate: null,
    targets: [],
    additionalPeople: [], // New field for additional people
    settings: {
      allowRetakes: false,
      sendReminders: true,
      reminderInterval: 3,
      showResults: true,
      anonymous: false,
      leaderboardType: 'none'
    }
  });

  const [questionnaireAllocationSummary, setQuestionnaireAllocationSummary] = useState([]);
  const [questionnaireDetailsLoading, setQuestionnaireDetailsLoading] = useState(false);
  const questionnaireSearchTimeoutRef = useRef(null);

  const isReadOnly = formData.status === 'active' || formData.status === 'completed';

  // Presence + auto-save
  const [otherEditors, setOtherEditors] = useState([]);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const [conflictInfo, setConflictInfo] = useState(null); // { savedByName, serverUpdatedAt, pendingData }
  const presenceTimerRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const serverUpdatedAtRef = useRef(null);
  const lastSavedJsonRef = useRef(null);

  useEffect(() => {
    loadInitialData();

    return () => {
      if (questionnaireSearchTimeoutRef.current) {
        clearTimeout(questionnaireSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isEdit && questionnaires.length > 0 && organizations.length > 0) {
      loadCampaignData();
    } else if (!isEdit) {
      // Set default date range for new campaigns (48 hours)
      form.setFieldsValue({
        dateRange: [dayjs(), dayjs().add(48, 'hour')]
      });
    }
  }, [id, questionnaires, organizations]);

  // panelActiveGroup defaults to null → "All" is selected on open

  // Debug: Monitor targets changes
  useEffect(() => {
    console.log('formData.targets changed:', formData.targets);
    console.log('Current step when targets changed:', currentStep);
  }, [formData.targets]);

  // Search users for People stage
  useEffect(() => {
    const searchUsers = async () => {
      if (!actualSearchQuery || actualSearchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const result = await smsService.searchUsers(actualSearchQuery);
        if (result.success) {
          setSearchResults(result.data?.data || []);
        } else {
          setSearchResults([]);
          messageApi.error('Failed to search users');
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
        messageApi.error('Error searching users');
      } finally {
        setSearchLoading(false);
      }
    };

    // Debounce search
    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [actualSearchQuery, messageApi]);

  const buildQuestionnaireAllocationSummary = (questionnaire, campaignIdToIgnore = null) => {
    if (!questionnaire) {
      return [];
    }

    const ignoreId = campaignIdToIgnore ? campaignIdToIgnore.toString() : null;
    const summarize = (entries, options = {}) => {
      return entries
        .filter(Boolean)
        .filter((entry) => {
          if (!entry.campaignId && !entry.campaignName) {
            return false;
          }

          if (ignoreId && entry.campaignId && entry.campaignId.toString() === ignoreId) {
            return false;
          }

          return true;
        })
        .map((entry) => ({
          campaignId: entry.campaignId,
          campaignName: entry.campaignName || 'Unnamed campaign',
          allocatedAt: entry.allocatedAt,
          allocatedByName: entry.allocatedByName || 'Unknown user',
          questionCount:
            typeof entry.questionCount === 'number'
              ? entry.questionCount
              : options.fallbackQuestionCount
        }))
        .sort((a, b) => new Date(b.allocatedAt || 0) - new Date(a.allocatedAt || 0));
    };

    if (Array.isArray(questionnaire.allocatedCampaigns) && questionnaire.allocatedCampaigns.length > 0) {
      return summarize(questionnaire.allocatedCampaigns);
    }

    if (Array.isArray(questionnaire.questions) && questionnaire.questions.length > 0) {
      // Legacy fallback: aggregate per-question metadata if it exists
      const map = new Map();

      questionnaire.questions.forEach((question) => {
        if (!Array.isArray(question.allocatedToCampaigns)) {
          return;
        }

        question.allocatedToCampaigns.forEach((entry) => {
          if (!entry) {
            return;
          }

          const key = entry.campaignId ? entry.campaignId.toString() : entry.campaignName;
          if (!key) {
            return;
          }

          if (ignoreId && entry.campaignId && entry.campaignId.toString() === ignoreId) {
            return;
          }

          const existing = map.get(key) || {
            campaignId: entry.campaignId,
            campaignName: entry.campaignName || 'Unnamed campaign',
            allocatedAt: entry.allocatedAt,
            allocatedByName: entry.allocatedByName || 'Unknown user',
            questionCount: 0
          };

          existing.questionCount += 1;

          if (entry.allocatedAt && (!existing.allocatedAt || new Date(entry.allocatedAt) > new Date(existing.allocatedAt))) {
            existing.allocatedAt = entry.allocatedAt;
          }

          map.set(key, existing);
        });
      });

      return Array.from(map.values()).sort((a, b) => new Date(b.allocatedAt || 0) - new Date(a.allocatedAt || 0));
    }

    return [];
  };

  const renderQuestionnaireAllocationSummary = (summary, options = {}) => {
    if (!summary || summary.length === 0) {
      return null;
    }

    const limit = options.limit || summary.length;
    const items = summary.slice(0, limit);

    return (
      <div style={{ marginTop: 8 }}>
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => (
            <List.Item style={{ padding: '4px 0' }}>
              <div style={{ width: '100%' }}>
                <div style={{ fontWeight: 600 }}>{item.campaignName}</div>
                <div style={{ fontSize: '12px', color: '#555' }}>
                    {typeof item.questionCount === 'number'
                      ? `${item.questionCount} question${item.questionCount === 1 ? '' : 's'}`
                      : 'Quiz reused'}
                    {item.allocatedByName ? ` • ${item.allocatedByName}` : ''}
                </div>
                {item.allocatedAt && (
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {dayjs(item.allocatedAt).format('MMM D, YYYY HH:mm')}
                  </div>
                )}
              </div>
            </List.Item>
          )}
        />
        {summary.length > limit && (
          <Text type="secondary">
            +{summary.length - limit} more allocation{summary.length - limit === 1 ? '' : 's'}
          </Text>
        )}
      </div>
    );
  };

  const getSelectedQuestionCount = () => {
    if (!selectedQuestionnaireDetails) {
      return 0;
    }

    if (Array.isArray(selectedQuestionnaireDetails.questions)) {
      return selectedQuestionnaireDetails.questions.length;
    }

    if (typeof selectedQuestionnaireDetails.questionCount === 'number') {
      return selectedQuestionnaireDetails.questionCount;
    }

    return 0;
  };

  const resetReuseModalState = () => {
    setReuseModalState({
      open: false,
      summary: [],
      questionnaire: null,
      questionnaireId: null,
      previousQuestionnaireId: null
    });
  };

  const applyQuestionnaireSelection = (details, summary, questionnaireIdValue) => {
    setSelectedQuestionnaireDetails(details);
    setQuestionnaireAllocationSummary(summary || []);
    setFormData(prev => ({ ...prev, questionnaire: questionnaireIdValue }));
  };

  const handleReuseModalConfirm = () => {
    if (reuseModalState.questionnaire && reuseModalState.questionnaireId) {
      applyQuestionnaireSelection(
        reuseModalState.questionnaire,
        reuseModalState.summary,
        reuseModalState.questionnaireId
      );
    }
    resetReuseModalState();
  };

  const handleReuseModalCancel = () => {
    form.setFieldsValue({ questionnaire: reuseModalState.previousQuestionnaireId || null });
    resetReuseModalState();
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load questionnaires
      const questionnaireResponse = await questionnaireService.getAll({ limit: 20 });
      if (questionnaireResponse.success) {
        setQuestionnaires(questionnaireResponse.data || []);
      }

      // Load organizations
      const orgResponse = await campaignService.getOrganizationsForTargeting();
      console.log('Organization response:', orgResponse);
      if (orgResponse.success) {
        console.log('Organization data:', orgResponse.data);
        const sorted = (orgResponse.data || [])
          .sort((a, b) => a.title.localeCompare(b.title))
          .map(g => ({ ...g, children: [...(g.children || [])].sort((a, b) => a.title.localeCompare(b.title)) }));
        setOrganizations(sorted);
      } else {
        console.error('Failed to load organizations:', orgResponse.message);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      messageApi.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignData = async () => {
    try {
      const response = await campaignService.getById(id);
      if (response.success) {
        const campaign = response.data;
        serverUpdatedAtRef.current = campaign.updatedAt || null;
        console.log('Loading campaign data:', campaign);
        
        // Update form data
        setFormData({
          name: campaign.name,
          description: campaign.description,
          questionnaire: campaign.questionnaire._id,
          startDate: campaign.startDate,
          endDate: campaign.endDate,
          targets: campaign.targets || [],
          displayPaths: campaign.targetPaths?.displayPaths || [],
          technicalPaths: campaign.targetPaths?.technicalPaths || [],
          additionalPeople: campaign.additionalPeople || [],
          settings: campaign.settings || formData.settings,
          status: campaign.status // Add campaign status
        });
        
        // Load additional people details if they exist
        if (campaign.additionalPeople && campaign.additionalPeople.length > 0) {
          // Handle new format: { user: {...}, includeInResults: bool } 
          // or old format: just user objects
          const mappedPeople = campaign.additionalPeople.map(p => {
            // New format: has 'user' property with the populated user object
            if (p.user && typeof p.user === 'object') {
              return { 
                ...p.user, 
                includeInResults: p.includeInResults !== false 
              };
            }
            // Old format: just the user object
            if (typeof p === 'object' && p._id) {
              return { ...p, includeInResults: true };
            }
            // Just IDs - shouldn't happen if properly populated
            return null;
          }).filter(Boolean);
          
          setSelectedPeople(mappedPeople);
        }
        
        // Set form values
        form.setFieldsValue({
          name: campaign.name,
          description: campaign.description,
          questionnaire: campaign.questionnaire._id,
          dateRange: [
            campaign.startDate ? dayjs(campaign.startDate) : null,
            campaign.endDate ? dayjs(campaign.endDate) : null
          ]
        });

        // Set selected questionnaire details for preview
        if (campaign.questionnaire) {
          setQuestionnaires((prev) => {
            if (prev.some(q => q._id === campaign.questionnaire._id)) {
              return prev;
            }
            return [campaign.questionnaire, ...prev];
          });

          // First try to use the populated questionnaire data
          setSelectedQuestionnaireDetails(campaign.questionnaire);
          setQuestionnaireAllocationSummary(
            buildQuestionnaireAllocationSummary(campaign.questionnaire, campaign._id?.toString())
          );
          
          // Also try to find it in the questionnaires list for full details
          const fullQuestionnaire = questionnaires.find(q => q._id === campaign.questionnaire._id);
          if (fullQuestionnaire?.questions?.length) {
            setSelectedQuestionnaireDetails(fullQuestionnaire);
            setQuestionnaireAllocationSummary(
              buildQuestionnaireAllocationSummary(fullQuestionnaire, campaign._id?.toString())
            );
          }
        } else {
          setQuestionnaireAllocationSummary([]);
        }

        // Convert targetPaths back to TreeSelect format for editing
        if (campaign.targetPaths?.technicalPaths && campaign.targetPaths.technicalPaths.length > 0) {
          console.log('Converting technical paths to TreeSelect format...');
          console.log('Technical paths:', campaign.targetPaths.technicalPaths);
          console.log('Available organizations:', organizations);
          
          const convertedTargets = convertTechnicalPathsToTreeSelectValues(
            campaign.targetPaths.technicalPaths,
            organizations
          );
          
          console.log('Converted targets for TreeSelect:', convertedTargets);
          setSelectedTargets(convertedTargets);
          
          // Also set form field
          form.setFieldsValue({
            targets: convertedTargets
          });
        }
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
      messageApi.error('Failed to load campaign data');
    }
  };

  // Helper function to convert technical paths back to TreeSelect values
  const convertTechnicalPathsToTreeSelectValues = (technicalPaths, organizationTree) => {
    const treeSelectValues = [];
    
    technicalPaths.forEach(technicalPath => {
      // Parse technical path: "Group A/68ec075b08e37057229f4246/IT Support"
      const parts = technicalPath.split('/');
      if (parts.length !== 3) {
        console.warn('Invalid technical path format:', technicalPath);
        return;
      }
      
      const [groupName, businessUnitId, departmentName] = parts;
      
      // Find the matching department in the organization tree
      organizationTree.forEach(group => {
        if (group.title === groupName) {
          group.children?.forEach(businessUnit => {
            if (businessUnit.key === businessUnitId) {
              businessUnit.children?.forEach(department => {
                if (department.title === departmentName) {
                  // Found the matching department, add its TreeSelect value
                  treeSelectValues.push(department.value);
                }
              });
            }
          });
        }
      });
    });
    
    console.log('Converted technical paths to TreeSelect values:', treeSelectValues);
    return treeSelectValues;
  };

  const steps = [
    {
      title: 'Overview',
      icon: <FileTextOutlined />,
      content: 'Campaign details'
    },
    {
      title: 'Quiz',
      icon: <QuestionCircleOutlined />,
      content: 'Select quiz'
    },
    {
      title: 'Target',
      icon: <TeamOutlined />,
      content: 'Select participants'
    },
    {
      title: 'People',
      icon: <UserOutlined />,
      content: 'Add additional people'
    },
    {
      title: 'Review',
      icon: <CheckCircleOutlined />,
      content: 'Review and confirm'
    }
  ];

  // Auto-save: send current formData to the server (only for existing campaigns)
  const performAutoSave = useCallback(async (data) => {
    if (!isEdit || isReadOnly) return;
    const snapshot = JSON.stringify(data);
    if (snapshot === lastSavedJsonRef.current) return;
    try {
      setAutoSaveStatus('saving');
      const payload = { ...data, autoSave: true, clientUpdatedAt: serverUpdatedAtRef.current };
      const response = await campaignService.update(id, payload);
      if (response.conflict) {
        setAutoSaveStatus('error');
        setConflictInfo({ savedByName: response.savedByName, serverUpdatedAt: response.serverUpdatedAt, pendingData: data });
        return;
      }
      if (response.success) {
        lastSavedJsonRef.current = snapshot;
        setAutoSaveStatus('saved');
        if (response.data?.updatedAt) serverUpdatedAtRef.current = response.data.updatedAt;
      } else {
        setAutoSaveStatus('error');
      }
    } catch {
      setAutoSaveStatus('error');
    }
  }, [isEdit, isReadOnly, id]);

  // Auto-save: debounce 5s after last formData change
  useEffect(() => {
    if (!isEdit || isReadOnly) return;
    setAutoSaveStatus('idle');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave(formData);
    }, 5000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Presence: ping every 30s while editing an existing campaign
  useEffect(() => {
    if (!isEdit) return;
    const ping = async () => {
      const result = await campaignService.presencePing(id);
      if (result?.editors) setOtherEditors(result.editors);
    };
    ping();
    presenceTimerRef.current = setInterval(ping, 30000);
    return () => {
      clearInterval(presenceTimerRef.current);
      campaignService.presenceLeave(id);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        // Validate overview fields
        const values = await form.validateFields(['name', 'description', 'dateRange']);
        setFormData(prev => ({
          ...prev,
          name: values.name,
          description: values.description,
          startDate: values.dateRange[0].toISOString(),
          endDate: values.dateRange[1].toISOString()
        }));
      } else if (currentStep === 1) {
        // Validate questionnaire selection
        const values = await form.validateFields(['questionnaire']);
        setFormData(prev => ({
          ...prev,
          questionnaire: values.questionnaire
        }));
      } else if (currentStep === 2) {
        // Target selection is optional here — a campaign can also be built entirely
        // from individually-added people on the next step. Final requirement (at
        // least one of target/people) is enforced by validateCampaignData on submit.
        const processedTargets = processSelectedTargets();
        setFormData(prev => ({
          ...prev,
          targets: processedTargets
        }));
      } else if (currentStep === 3) {
        // People stage - optional, store selected people with includeInResults
        setFormData(prev => ({
          ...prev,
          additionalPeople: selectedPeople.map(p => ({ 
            user: p._id, 
            includeInResults: p.includeInResults !== false 
          }))
        }));
      }
      
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  // Handle clicking on a step to jump directly to it
  const handleStepClick = async (targetStep) => {
    // Don't allow navigation if campaign is readonly
    if (isReadOnly) return;
    
    // Don't allow going backwards without validation
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      return;
    }
    
    // For forward navigation, validate current step before proceeding
    try {
      if (currentStep === 0 && targetStep > 0) {
        // Validate overview fields before moving forward
        const values = await form.validateFields(['name', 'description', 'dateRange']);
        setFormData(prev => ({
          ...prev,
          name: values.name,
          description: values.description,
          startDate: values.dateRange[0].toISOString(),
          endDate: values.dateRange[1].toISOString()
        }));
      }
      
      if (currentStep === 1 && targetStep > 1) {
        // Validate questionnaire selection before moving forward
        const values = await form.validateFields(['questionnaire']);
        setFormData(prev => ({
          ...prev,
          questionnaire: values.questionnaire
        }));
      }
      
      if (currentStep === 2 && targetStep > 2) {
        // Target selection is optional here — see handleNext for why.
        const processedTargets = processSelectedTargets();
        setFormData(prev => ({
          ...prev,
          targets: processedTargets
        }));
      }
      
      if (currentStep === 3 && targetStep > 3) {
        // People stage - optional, store selected people with includeInResults
        setFormData(prev => ({
          ...prev,
          additionalPeople: selectedPeople.map(p => ({ 
            user: p._id, 
            includeInResults: p.includeInResults !== false 
          }))
        }));
      }
      
      setCurrentStep(targetStep);
    } catch (error) {
      console.error('Validation failed when jumping to step:', error);
      messageApi.error('Please complete the current step before proceeding');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      
      // Process selected targets (legacy format for backward compatibility)
      const processedTargets = processSelectedTargets();
      
      const campaignData = {
        ...formData,
        targets: processedTargets,
        // New path-based targeting structure
        targetPaths: {
          displayPaths: formData.displayPaths || [],
          technicalPaths: formData.technicalPaths || []
        },
        // Include additional people
        additionalPeople: formData.additionalPeople || []
      };

      console.log('Sending campaign data with new path structure:', {
        displayPaths: formData.displayPaths || [],
        technicalPaths: formData.technicalPaths || [],
        legacyTargets: processedTargets.length,
        additionalPeople: formData.additionalPeople?.length || 0
      });

      // Validate campaign data
      console.log('Validating campaign data:', {
        targets: campaignData.targets?.length || 0,
        displayPaths: campaignData.targetPaths?.displayPaths?.length || 0,
        technicalPaths: campaignData.targetPaths?.technicalPaths?.length || 0
      });
      
      const validation = campaignService.validateCampaignData(campaignData);
      console.log('Validation result:', validation);
      
      if (!validation.isValid) {
        messageApi.error(validation.errors[0]);
        return;
      }

      let response;
      if (isEdit) {
        response = await campaignService.update(id, { ...campaignData, clientUpdatedAt: serverUpdatedAtRef.current });
      } else {
        response = await campaignService.create(campaignData);
      }

      if (response.conflict) {
        setConflictInfo({ savedByName: response.savedByName, serverUpdatedAt: response.serverUpdatedAt, pendingData: campaignData });
        return;
      }

      if (response.success) {
        if (response.data?.updatedAt) serverUpdatedAtRef.current = response.data.updatedAt;
        messageApi.success(`Campaign ${isEdit ? 'updated' : 'created'} successfully`);
        navigate('/dashboard/campaigns');
      } else {
        messageApi.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} campaign`);
      }
    } catch (error) {
      console.error('Error submitting campaign:', error);
      messageApi.error(`Failed to ${isEdit ? 'update' : 'create'} campaign`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleLaunchCampaign = async () => {
    try {
      setLaunchLoading(true);
      
      let campaignIdToLaunch = id;
      
      // First save the campaign if not already saved
      if (!isEdit) {
        // Need to create campaign first
        const processedTargets = processSelectedTargets();
        
        const campaignData = {
          ...formData,
          targets: processedTargets,
          targetPaths: {
            displayPaths: formData.displayPaths || [],
            technicalPaths: formData.technicalPaths || []
          },
          // Explicitly include additional people with includeInResults defaulting to true
          additionalPeople: selectedPeople.map(p => ({ 
            user: p._id, 
            includeInResults: p.includeInResults !== false 
          }))
        };
        
        const validation = campaignService.validateCampaignData(campaignData);
        if (!validation.isValid) {
          messageApi.error(validation.errors[0]);
          return;
        }
        
        const createResponse = await campaignService.create(campaignData);
        if (!createResponse.success) {
          messageApi.error(createResponse.message || 'Failed to create campaign');
          return;
        }
        
        campaignIdToLaunch = createResponse.data._id;
        messageApi.success('Campaign created successfully');
      }
      
      // Now launch the campaign
      const launchResponse = await campaignService.launchCampaign(campaignIdToLaunch);
      
      if (launchResponse.success) {
        const { participantsTargeted, targetsCreated } = launchResponse.data;
        messageApi.success(
          `Campaign launched successfully! ${participantsTargeted} participants targeted, ${targetsCreated} new targets created.`
        );
        setShowLaunchModal(false);
        navigate('/dashboard/campaigns');
      } else {
        messageApi.error(launchResponse.message || 'Failed to launch campaign');
      }
      
    } catch (error) {
      console.error('Error launching campaign:', error);
      messageApi.error('Failed to launch campaign');
    } finally {
      setLaunchLoading(false);
    }
  };

  const processSelectedTargets = () => {
    const targets = [];
    const organizationMap = new Map();

    selectedTargets.forEach(value => {
      // Check if this is a department selection (has multiple parts and doesn't start with group-)
      const parts = value.split('-');
      if (parts.length >= 3 && !value.startsWith('group-')) {
        // This is a department selection: group-business-dept format
        // Find the business unit and department in the nested structure
        let businessUnit = null;
        let department = null;
        
        organizations.forEach(managingGroup => {
          managingGroup.children?.forEach(bu => {
            bu.children?.forEach(dept => {
              if (dept.value === value) {
                businessUnit = bu;
                department = dept;
              }
            });
          });
        });
        
        if (businessUnit && department) {
          const orgId = businessUnit.key;
          
          if (!organizationMap.has(orgId)) {
            organizationMap.set(orgId, {
              organizationId: orgId,
              organizationName: businessUnit.title || '',
              departments: []
            });
          }
          
          organizationMap.get(orgId).departments.push({
            departmentId: department.key || department.title, // Use key if available, fallback to title
            departmentName: department.title
          });
        }
      } else if (parts.length === 2 && !value.startsWith('group-')) {
        // This is a business unit selection: group-business format
        let businessUnit = null;
        
        organizations.forEach(managingGroup => {
          managingGroup.children?.forEach(bu => {
            if (bu.value === value) {
              businessUnit = bu;
            }
          });
        });
        
        if (businessUnit && !organizationMap.has(businessUnit.key)) {
          organizationMap.set(businessUnit.key, {
            organizationId: businessUnit.key,
            organizationName: businessUnit.title,
            departments: [] // Empty means entire organization
          });
        }
      }
      // Note: We ignore managing group selections (group-xxx)
    });

    return Array.from(organizationMap.values());
  };

  // Handle questionnaire selection
  const handleQuestionnaireChange = async (questionnaireId) => {
    const previousQuestionnaireId = formData.questionnaire;

    if (!questionnaireId) {
      setSelectedQuestionnaireDetails(null);
      setQuestionnaireAllocationSummary([]);
      setFormData(prev => ({ ...prev, questionnaire: null }));
      return;
    }

    setQuestionnaireDetailsLoading(true);
    try {
      const response = await questionnaireService.getById(questionnaireId);

      if (!response.success) {
        messageApi.error(response.message || 'Failed to load quiz details');
        form.setFieldsValue({ questionnaire: previousQuestionnaireId || null });
        return;
      }

      const questionnaireDetails = response.data;
      const summary = buildQuestionnaireAllocationSummary(
        questionnaireDetails,
        isEdit ? id : null
      );

      if (!isReadOnly && summary.length > 0) {
        setReuseModalState({
          open: true,
          summary,
          questionnaire: questionnaireDetails,
          questionnaireId,
          previousQuestionnaireId: previousQuestionnaireId || null
        });
      } else {
        applyQuestionnaireSelection(questionnaireDetails, summary, questionnaireId);
      }
    } catch (error) {
      console.error('Error fetching quiz details:', error);
      messageApi.error('Failed to load quiz details');
      form.setFieldsValue({ questionnaire: previousQuestionnaireId || null });
    } finally {
      setQuestionnaireDetailsLoading(false);
    }
  };

  // ── Advanced Targeting ────────────────────────────────────────────────
  // Walks the already-loaded organizations tree and returns all dept.value
  // Department values whose title matches any of `keywords` using word-boundary matching.
  // "IT" will match "IT", "IT Department", but NOT "Finance & IT - Dev"
  // Excludes departments from groups that are manually deselected
  // matchMode: 'word' (default) = word-boundary, 'exact' = full match, 'contains' = substring
  const getAdvancedMatchingValues = (keywords, buFilter, excludeGroups = new Set(), matchMode = 'word') => {
    const kws = (keywords || []).map(k => k.toLowerCase()).filter(Boolean);
    const matchingValues = [];
    (organizations || []).forEach(group => {
      // Skip this group if manually deselected
      if (excludeGroups.has(group.title)) return;
      
      (group.children || []).forEach(bu => {
        if (buFilter && buFilter.length > 0 && !buFilter.includes(bu.title)) return;
        (bu.children || []).forEach(dept => {
          const deptLower = dept.title.toLowerCase();
          if (!kws.length || kws.some(kw => {
            if (matchMode === 'exact') {
              // Full match: department name must exactly equal the keyword
              return deptLower === kw;
            } else if (matchMode === 'contains') {
              // Contains: keyword appears anywhere in the department name
              return deptLower.includes(kw);
            } else {
              // Word-boundary matching (default): keyword as a whole word
              const regex = new RegExp(`(^|\\s|-|[&/])${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|\\s|-|[&/])`, 'i');
              return regex.test(deptLower);
            }
          })) {
            matchingValues.push(dept.value);
          }
        });
      });
    });
    return matchingValues;
  };

  // All active keywords = confirmed tags + whatever is currently typed
  const advAllKeywords = [
    ...advTargetKeywords,
    ...(advTargetInput.trim() ? [advTargetInput.trim()] : [])
  ];

  // Whether the OK button should be enabled
  const advHasCriteria = advAllKeywords.length > 0 || advTargetBUs.length > 0;

  // Memoized match results — recomputed only when filter inputs or org tree changes
  const advMatchedValues = useMemo(() => {
    const kws = [...advTargetKeywords, ...(advTargetInput.trim() ? [advTargetInput.trim()] : [])];
    if (!kws.length && !advTargetBUs.length) return [];
    return getAdvancedMatchingValues(kws, advTargetBUs, advTargetExcludedGroups, advTargetMatchMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advTargetKeywords, advTargetInput, advTargetBUs, advTargetExcludedGroups, advTargetMatchMode, organizations]);

  // Auto-add newly matched departments to the included set whenever filters change (fix: no longer requires preview to be open)
  useEffect(() => {
    if (advMatchedValues.length > 0) {
      setAdvTargetIncludedDepts(prev => new Set([...prev, ...advMatchedValues]));
    }
  }, [advMatchedValues]);

  const advTargetPreview = useMemo(() => {
    if (!advMatchedValues.length) return null;
    const orgs = new Set();
    advMatchedValues.forEach(v => {
      (organizations || []).forEach(group => {
        (group.children || []).forEach(bu => {
          (bu.children || []).forEach(dept => {
            if (dept.value === v) orgs.add(bu.key);
          });
        });
      });
    });
    return { depts: advMatchedValues.length, orgs: orgs.size };
  }, [advMatchedValues, organizations]);

  const handleAdvancedTargetApply = () => {
    if (!advMatchedValues.length) {
      messageApi.warning('No departments matched your criteria');
      return;
    }
    const toAdd = advMatchedValues.filter(dept => advTargetIncludedDepts.has(dept));
    if (!toAdd.length) {
      messageApi.warning('Please select at least one department to add');
      return;
    }
    const alreadySelected = toAdd.filter(d => selectedTargets.includes(d));
    const netNew = toAdd.filter(d => !selectedTargets.includes(d));
    const merged = Array.from(new Set([...selectedTargets, ...toAdd]));
    handleTargetChange(merged);
    setAdvTargetVisible(false);
    setAdvTargetKeywords([]);
    setAdvTargetInput('');
    setAdvTargetBUs([]);
    setAdvTargetExcludedGroups(new Set());
    setAdvTargetPreviewExpanded(false);
    setAdvTargetIncludedDepts(new Set());
    if (netNew.length > 0) {
      messageApi.success(
        `Added ${netNew.length} department${netNew.length === 1 ? '' : 's'}` +
        (alreadySelected.length > 0 ? ` (${alreadySelected.length} already selected)` : '') +
        ` across ${advTargetPreview?.orgs ?? '?'} organisation${(advTargetPreview?.orgs ?? 0) === 1 ? '' : 's'}`
      );
    } else {
      messageApi.info(`All ${alreadySelected.length} department${alreadySelected.length === 1 ? '' : 's'} were already selected`);
    }
  };
  const handleSaveFilter = async () => {
    const filterName = advSaveFilterName.trim();
    if (!filterName) { messageApi.error('Enter a filter name'); return; }
    const existing = advSavedFilters.find(f => f.name === filterName);
    let res;
    if (existing) {
      res = await settingsService.updateTargetingFilter(filterName, filterName, advAllKeywords, advTargetMatchMode, advTargetBUs);
    } else {
      res = await settingsService.addTargetingFilter(filterName, advAllKeywords, advTargetMatchMode, advTargetBUs);
    }
    if (res.success) {
      messageApi.success(existing ? 'Filter updated' : 'Filter saved');
      const r = await settingsService.getTargetingFilters();
      if (r.success) setAdvSavedFilters(r.data || []);
    } else {
      messageApi.error(res.message || 'Failed to save filter');
    }
  };
  // ─────────────────────────────────────────────────────────────────────

  const handleTargetChange = (selectedValues) => {
    console.log('Selected targets:', selectedValues);
    setSelectedTargets(selectedValues);
    
    // Build both technical IDs and display paths
    const organizationMap = new Map();
    const processedDepartments = new Set();
    const displayPaths = []; // For user-friendly display
    const technicalPaths = []; // For backend processing

    selectedValues.forEach(target => {
      let found = false;
      
      organizations.forEach(group => {
        if (!found && group.value === target) {
          // Managing Group selection - include all departments
          group.children?.forEach(bu => {
            bu.children?.forEach(dept => {
              const orgId = bu.key;
              const deptKey = dept.value;
              
              if (!processedDepartments.has(deptKey)) {
                processedDepartments.add(deptKey);
                
                // Technical path: group/businessunitid/department
                const technicalPath = `${group.title}/${bu.key}/${dept.title}`;
                technicalPaths.push(technicalPath);
                
                // Display path: group/businessunitname/department  
                const displayPath = `${group.title}/${bu.title}/${dept.title}`;
                displayPaths.push(displayPath);
                
                if (!organizationMap.has(orgId)) {
                  organizationMap.set(orgId, {
                    organizationId: orgId,
                    organizationName: bu.title,
                    managingGroup: group.title,
                    departments: []
                  });
                }
                organizationMap.get(orgId).departments.push({ 
                  departmentId: dept.key,
                  departmentName: dept.title 
                });
              }
            });
          });
          found = true;
        } else if (!found) {
          group.children?.forEach(bu => {
            if (!found && bu.value === target) {
              // Business Unit selection - include all its departments
              bu.children?.forEach(dept => {
                const orgId = bu.key;
                const deptKey = dept.value;
                
                if (!processedDepartments.has(deptKey)) {
                  processedDepartments.add(deptKey);
                  
                  // Technical path: group/businessunitid/department
                  const technicalPath = `${group.title}/${bu.key}/${dept.title}`;
                  technicalPaths.push(technicalPath);
                  
                  // Display path: group/businessunitname/department
                  const displayPath = `${group.title}/${bu.title}/${dept.title}`;
                  displayPaths.push(displayPath);
                  
                  if (!organizationMap.has(orgId)) {
                    organizationMap.set(orgId, {
                      organizationId: orgId,
                      organizationName: bu.title,
                      managingGroup: group.title,
                      departments: []
                    });
                  }
                  organizationMap.get(orgId).departments.push({ 
                    departmentId: dept.key,
                    departmentName: dept.title 
                  });
                }
              });
              found = true;
            } else if (!found) {
              bu.children?.forEach(dept => {
                if (!found && dept.value === target) {
                  // Department selection
                  const orgId = bu.key;
                  const deptKey = dept.value;
                  
                  if (!processedDepartments.has(deptKey)) {
                    processedDepartments.add(deptKey);
                    
                    // Technical path: group/businessunitid/department
                    const technicalPath = `${group.title}/${bu.key}/${dept.title}`;
                    technicalPaths.push(technicalPath);
                    
                    // Display path: group/businessunitname/department
                    const displayPath = `${group.title}/${bu.title}/${dept.title}`;
                    displayPaths.push(displayPath);
                    
                    if (!organizationMap.has(orgId)) {
                      organizationMap.set(orgId, {
                        organizationId: orgId,
                        organizationName: bu.title,
                        managingGroup: group.title,
                        departments: []
                      });
                    }
                    organizationMap.get(orgId).departments.push({ 
                      departmentId: dept.key,
                      departmentName: dept.title 
                    });
                  }
                  found = true;
                }
              });
            }
          });
        }
      });
    });

    const targetsArray = Array.from(organizationMap.values());
    
    console.log('Technical paths:', technicalPaths);
    console.log('Display paths:', displayPaths);
    console.log('Final targets array:', targetsArray);
    
    setFormData(prev => ({
      ...prev,
      targets: targetsArray,
      technicalPaths: technicalPaths,
      displayPaths: displayPaths
    }));
  };

  // People stage handlers
  const handleSearch = (value) => {
    setSearchQuery(value);
    if (value && value.length >= 2) {
      setActualSearchQuery(value);
    } else if (value.length === 0) {
      setActualSearchQuery('');
      setSearchResults([]);
    }
  };

  const handleQuestionnaireSearch = (value) => {
    if (questionnaireSearchTimeoutRef.current) {
      clearTimeout(questionnaireSearchTimeoutRef.current);
    }

    questionnaireSearchTimeoutRef.current = setTimeout(async () => {
      const query = value?.trim() || '';
      setQuestionnaireOptionsLoading(true);

      try {
        const response = await questionnaireService.getAll(
          query.length >= 2
            ? { search: query, fetchAll: true, limit: 100 }
            : { limit: 20 }
        );

        if (response.success) {
          setQuestionnaires(response.data || []);
        } else {
          setQuestionnaires([]);
        }
      } catch (error) {
        console.error('Error searching quizzes:', error);
        setQuestionnaires([]);
      } finally {
        setQuestionnaireOptionsLoading(false);
      }
    }, 300);
  };

  const handleAddPerson = (person) => {
    // Check if person is already selected
    if (!selectedPeople.find(p => p._id === person._id)) {
      // Add person with includeInResults defaulting to true
      setSelectedPeople([...selectedPeople, { ...person, includeInResults: true }]);
    }
    // Clear search
    setSearchQuery('');
    setActualSearchQuery('');
    setSearchResults([]);
  };

  const handleRemovePerson = (personId) => {
    setSelectedPeople(selectedPeople.filter(p => p._id !== personId));
  };

  const handleTogglePersonIncludeInResults = (personId) => {
    setSelectedPeople(selectedPeople.map(p =>
      p._id === personId ? { ...p, includeInResults: !p.includeInResults } : p
    ));
  };

  // Merge people resolved from a CSV import (matched or newly created) into
  // the same selectedPeople list the search-and-add flow builds.
  const handleCsvPeopleResolved = (people) => {
    setSelectedPeople(prev => {
      const existingIds = new Set(prev.map(p => p._id));
      const toAdd = people
        .filter(p => !existingIds.has(p._id))
        .map(p => ({ ...p, includeInResults: true }));
      return [...prev, ...toAdd];
    });
  };

  // Flat list of {id, name} business units, derived from the same organizations
  // tree used for department targeting, for the CSV "create new person" flow.
  const businessUnitOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    (organizations || []).forEach(group => {
      (group.children || []).forEach(bu => {
        if (!seen.has(bu.key)) {
          seen.add(bu.key);
          options.push({ id: bu.key, name: bu.title });
        }
      });
    });
    return options;
  }, [organizations]);

  const renderInteractiveTargetManager = () => {
    if (!selectedTargets.length) return null;

    // Collect names of currently-selected depts so we can surface matching non-selected BUs
    const selectedDeptNames = new Set();
    (organizations || []).forEach(group =>
      (group.children || []).forEach(bu =>
        (bu.children || []).forEach(dept => {
          if (selectedTargets.includes(dept.value)) selectedDeptNames.add(dept.title);
        })
      )
    );

    // Build group → BU rows, including non-selected BUs that share a dept type
    const groupRows = [];
    (organizations || []).forEach(group => {
      const buRows = [];
      (group.children || []).forEach(bu => {
        const buDepts = bu.children || [];
        const selected = buDepts.filter(d => selectedTargets.includes(d.value));
        const available = buDepts.filter(d => !selectedTargets.includes(d.value) && selectedDeptNames.has(d.title));
        if (selected.length > 0 || available.length > 0) {
          buRows.push({ bu, selected, available });
        }
      });
      if (buRows.length > 0) groupRows.push({ group, buRows });
    });

    const totalSelected = selectedTargets.length;
    const totalBUs = groupRows.reduce((n, g) => n + g.buRows.filter(r => r.selected.length > 0).length, 0);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Text strong style={{ fontSize: 13 }}>
            {totalSelected} dept{totalSelected !== 1 ? 's' : ''} across {totalBUs} business unit{totalBUs !== 1 ? 's' : ''}
          </Text>
          {!isReadOnly && (
            <Button size="small" type="link" danger onClick={() => handleTargetChange([])}>
              Clear all
            </Button>
          )}
        </div>

        {groupRows.map(({ group, buRows }) => {
          const groupSelectedValues = buRows.flatMap(r => r.selected.map(d => d.value));
          const allBUsSelected = buRows.every(r => r.selected.length > 0);

          return (
            <div key={group.title} style={{ marginBottom: 12 }}>
              {/* Group header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#f5f5f5', borderRadius: '6px 6px 0 0', border: '1px solid #e8e8e8', borderBottom: 'none' }}>
                {!isReadOnly && (
                  <Checkbox
                    checked={allBUsSelected}
                    indeterminate={!allBUsSelected && groupSelectedValues.length > 0}
                    onChange={e => {
                      if (!e.target.checked) {
                        handleTargetChange(selectedTargets.filter(d => !groupSelectedValues.includes(d)));
                      } else {
                        const toAdd = buRows.flatMap(r => r.available.map(d => d.value));
                        handleTargetChange(Array.from(new Set([...selectedTargets, ...toAdd])));
                      }
                    }}
                  />
                )}
                <FolderOutlined style={{ color: '#267a96', fontSize: 13 }} />
                <Text strong style={{ fontSize: 13, flex: 1 }}>{group.title}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {buRows.filter(r => r.selected.length > 0).length} / {buRows.length} BU{buRows.length !== 1 ? 's' : ''} included
                </Text>
              </div>

              {/* BU rows */}
              <div style={{ border: '1px solid #e8e8e8', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
                {buRows.map(({ bu, selected, available }, idx) => {
                  const isIncluded = selected.length > 0;
                  const selectedValues = selected.map(d => d.value);
                  return (
                    <div
                      key={bu.key}
                      style={{ padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: 8, borderBottom: idx < buRows.length - 1 ? '1px solid #f0f0f0' : 'none', background: isIncluded ? '#fff' : '#fafafa' }}
                    >
                      {!isReadOnly && (
                        <Checkbox
                          checked={isIncluded}
                          style={{ marginTop: 3 }}
                          onChange={e => {
                            if (!e.target.checked) {
                              handleTargetChange(selectedTargets.filter(d => !selectedValues.includes(d)));
                            } else {
                              const toAdd = available.map(d => d.value);
                              if (toAdd.length) handleTargetChange(Array.from(new Set([...selectedTargets, ...toAdd])));
                            }
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: isIncluded && selected.length > 0 ? 5 : 0 }}>
                          <BankOutlined style={{ fontSize: 12, color: isIncluded ? '#1890ff' : '#ccc' }} />
                          <span style={{ fontSize: 13, fontWeight: isIncluded ? 600 : 400, color: isIncluded ? '#262626' : '#aaa' }}>
                            {bu.title}
                          </span>
                          {!isIncluded && available.length > 0 && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              — {available.length} matching dept{available.length !== 1 ? 's' : ''} available
                            </Text>
                          )}
                        </div>
                        {isIncluded && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {[...selected].sort((a, b) => a.title.localeCompare(b.title)).map(dept => (
                              <Tag
                                key={dept.value}
                                closable={!isReadOnly}
                                onClose={() => handleTargetChange(selectedTargets.filter(d => d !== dept.value))}
                                style={{ fontSize: 11, margin: 0, borderRadius: 10 }}
                              >
                                {dept.title}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTargetSummary = () => {
    console.log('renderTargetSummary called with displayPaths:', formData.displayPaths);
    
    if (!formData.displayPaths || formData.displayPaths.length === 0) {
      console.log('No display paths found - showing empty state');
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No targets selected</Text>
        </div>
      );
    }

    // Group display paths by Managing Group → Business Unit → Department
    const groupedTargets = {};
    
    formData.displayPaths.forEach((displayPath, index) => {
      const parts = displayPath.split('/');
      
      if (parts.length >= 3) {
        const [groupName, businessUnit, department] = parts;
        
        if (!groupedTargets[groupName]) {
          groupedTargets[groupName] = {};
        }
        
        if (!groupedTargets[groupName][businessUnit]) {
          groupedTargets[groupName][businessUnit] = [];
        }
        
        groupedTargets[groupName][businessUnit].push({
          name: department,
          displayPath
        });
      }
    });

    console.log('Grouped targets for display:', groupedTargets);

    const totalDepartments = formData.displayPaths.length;

    return (
      <div>
        {/* Summary */}
        <div style={{ 
          marginBottom: '20px',
          padding: '12px 16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '6px',
          fontSize: '14px',
          color: '#666'
        }}>
          <strong>{totalDepartments}</strong> departments across <strong>{Object.keys(groupedTargets).length}</strong> groups
        </div>
        
        {/* Hierarchical tag layout */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {Object.entries(groupedTargets).map(([groupName, businessUnits]) => (
            <div key={groupName}>
              {/* Group header - simple bold text */}
              <div style={{ marginBottom: '8px' }}>
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: 'bold',
                  color: '#333',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <FolderOutlined style={{ color: '#2E86AB' }} />
                  {groupName}
                </span>
                <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                  ({Object.keys(businessUnits).length} business units)
                </span>
              </div>
              
              {/* Business units and departments */}
              <div style={{ marginLeft: '16px' }}>
                {Object.entries(businessUnits).map(([buName, departments]) => (
                  <div key={buName} style={{ marginBottom: '12px' }}>
                    {/* Business unit - simple bold text */}
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ 
                        fontSize: '13px', 
                        fontWeight: 'bold',
                        color: '#333',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <BankOutlined style={{ color: '#2E86AB' }} />
                        {buName}
                      </span>
                      <span style={{ fontSize: '11px', color: '#999', marginLeft: '6px' }}>
                        ({departments.length} departments)
                      </span>
                    </div>
                    
                    {/* Department tags */}
                    <div style={{ 
                      marginLeft: '16px',
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '4px'
                    }}>
                      {departments.map((dept, index) => (
                        <Tag
                          key={index}
                          style={{ 
                            fontSize: '12px',
                            margin: '2px',
                            borderRadius: '12px'
                          }}
                        >
                          {dept.name}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Title level={4} style={{ marginBottom: '24px' }}>Campaign Details</Title>
            
            <Form.Item
              name="name"
              label="Campaign name"
              rules={[{ required: true, message: 'Please enter campaign name' }]}
            >
              <Input placeholder="Enter campaign name" disabled={isReadOnly} />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <TextArea 
                rows={4} 
                placeholder="Add description"
                disabled={isReadOnly}
              />
            </Form.Item>

            <Form.Item
              name="dateRange"
              label="Campaign Period"
              rules={[{ required: true, message: 'Please select start and end dates' }]}
              initialValue={[dayjs(), dayjs().add(48, 'hour')]}
            >
              <RangePicker 
                style={{ width: '100%' }}
                format="YYYY-MM-DD HH:mm"
                placeholder={['Start date & time', 'End date & time']}
                showTime={{ 
                  format: 'HH:mm',
                  defaultValue: [dayjs('09:00', 'HH:mm'), dayjs('17:00', 'HH:mm')]
                }}
                disabled={isReadOnly}
              />
            </Form.Item>

            {/* Campaign Settings */}
            <Divider orientation="left">Campaign Settings</Divider>
            
            <Form.Item
              label="Leaderboard Display"
              tooltip="Show department leaderboard at the end of the quiz. Can be changed anytime, even during an active campaign."
            >
              <Radio.Group
                value={formData.settings.leaderboardType || 'none'}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, leaderboardType: e.target.value }
                }))}
              >
                <Space direction="vertical">
                  <Radio value="none">Don't show leaderboard</Radio>
                  <Radio value="presentation">Show percentage average</Radio>
                  <Radio value="points-percentage">Show points and percentage</Radio>
                  <Radio value="campaign-breakdown">Show percentage per campaign</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </div>
        );

      case 1:
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Title level={4} style={{ marginBottom: '24px' }}>Select a quiz</Title>
            
            <Form.Item
              name="questionnaire"
              rules={[{ required: true, message: 'Please select a quiz' }]}
            >
              <Select
                placeholder="Select a quiz"
                style={{ width: '100%' }}
                showSearch
                loading={questionnaireOptionsLoading}
                onChange={handleQuestionnaireChange}
                onSearch={handleQuestionnaireSearch}
                filterOption={false}
                disabled={isReadOnly}
              >
                {questionnaires.map(q => (
                  <Option key={q._id} value={q._id}>
                    {q.title}
                    {q.status === 'draft' && ' (incomplete)'}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Spin spinning={questionnaireDetailsLoading}>
              {selectedQuestionnaireDetails && (
                <Card
                  className="questionnaire-card"
                  style={{
                    marginTop: '24px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid #3a9bc1',
                    height: '220px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  bodyStyle={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '16px'
                  }}
                >
                <div style={{ position: 'absolute', top: 12, right: 12 }}>
                  <Space size="small">
                    <Tooltip title="Selected Quiz">
                      <Button
                        type="text"
                        size="small"
                        icon={<CheckCircleOutlined />}
                        style={{ 
                          padding: '0 4px', 
                          fontSize: '12px', 
                          color: '#1890ff',
                          cursor: 'default'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Tooltip>
                    <Tooltip title="Preview">
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`http://localhost:3001/preview/${selectedQuestionnaireDetails._id}`, '_blank');
                        }}
                        style={{ padding: '0 4px', fontSize: '12px', color: '#2E86AB' }}
                      />
                    </Tooltip>
                    {/* <Tooltip title="Edit">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/edit/${selectedQuestionnaireDetails._id}`, '_blank');
                        }}
                        style={{ padding: '0 4px', fontSize: '12px', color: '#2E86AB' }}
                      />
                    </Tooltip> */}
                  </Space>
                </div>

                <div className="questionnaire-card-content" style={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start'
                }}>
                  {/* Title and Description Section - Fixed Height */}
                  <div style={{ marginBottom: '8px', marginTop: '6px' }}>
                    <div style={{ 
                      height: '24px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: '4px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#2E86AB',
                      marginTop: '10px'
                    }}>
                      {selectedQuestionnaireDetails.title}
                    </div>
                    <div style={{ 
                      height: '40px',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      marginBottom: '4px',
                      fontSize: '14px', 
                      color: '#666'
                    }}>
                      {selectedQuestionnaireDetails?.description || 'No description'}
                    </div>
                  </div>

                  {/* Questions and Duration Section - Fixed Height */}
                  <div style={{ marginBottom: '4px', height: '20px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <Space size="large">
                        <Space size="small">
                          <QuestionCircleOutlined style={{ color: '#2E86AB' }} />
                          <span>{getSelectedQuestionCount()} questions</span>
                        </Space>
                        
                        {selectedQuestionnaireDetails.estimatedDuration && (
                          <Space size="small">
                            <ClockCircleOutlined style={{ color: '#2E86AB' }} />
                            <span>{selectedQuestionnaireDetails.estimatedDuration} min</span>
                          </Space>
                        )}
                      </Space>
                    </div>
                  </div>

                  {/* Footer Section - Fixed Height at Bottom */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    height: '20px', 
                    marginTop: 'auto' 
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                      <UserOutlined style={{ color: '#666', fontSize: '12px' }} />
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {selectedQuestionnaireDetails.createdBy?.name ? 
                          `${selectedQuestionnaireDetails.createdBy.name} ${selectedQuestionnaireDetails.createdBy.surname || ''}`.trim() :
                          'Unknown User'
                        }
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#999', marginLeft: '8px' }}>
                      {selectedQuestionnaireDetails.updatedAt ? 
                        `Updated ${new Date(selectedQuestionnaireDetails.updatedAt).toLocaleDateString()}` :
                        `Created ${new Date(selectedQuestionnaireDetails.createdAt).toLocaleDateString()}`
                      }
                    </div>
                  </div>
                </div>
              </Card>
              )}

              {selectedQuestionnaireDetails && questionnaireAllocationSummary.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: '16px' }}
                  message="This quiz is already part of other campaigns"
                  description={renderQuestionnaireAllocationSummary(questionnaireAllocationSummary, { limit: 5 })}
                />
              )}
            </Spin>
          </div>
        );

      case 2:
        return (
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <Title level={4} style={{ marginBottom: '24px' }}>Select Campaign Targets</Title>
            
            <Card>
              {/* Search + Advanced row */}
              <div style={{ marginBottom: 10 }}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                  placeholder="Search departments (word match — 'IT' won't match 'Italy')..."
                  value={panelSearch}
                  onChange={e => { setPanelSearch(e.target.value); }}
                  allowClear
                  disabled={isReadOnly}
                />
              </div>

              {/* Saved selections bar */}
              <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#e8f4fb', border: '1px solid #91c8e0', borderRadius: 8, padding: '8px 12px' }}>
                <Select
                  placeholder="Load saved selection..."
                  size="small"
                  style={{ minWidth: 200, flex: 1 }}
                  allowClear
                  value={undefined}
                  onFocus={() => settingsService.getTargetSelections().then(r => { if (r.success) setSavedSelections(r.data || []); })}
                  onChange={selName => {
                    const sel = savedSelections.find(s => s.name === selName);
                    if (!sel) return;
                    const converted = convertTechnicalPathsToTreeSelectValues(sel.technicalPaths || [], organizations);
                    handleTargetChange(converted);
                    setSavedSelectionName(sel.name);
                    messageApi.success(`Loaded "${sel.name}"`);
                  }}
                  options={savedSelections.map(s => ({
                    value: s.name,
                    label: (
                      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{s.name}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#999', fontSize: 11 }}>{s.technicalPaths?.length || 0} dept{s.technicalPaths?.length !== 1 ? 's' : ''}</span>
                          <CloseOutlined
                            style={{ color: '#ff4d4f', fontSize: 10 }}
                            onClick={async e => {
                              e.stopPropagation();
                              const res = await settingsService.removeTargetSelection(s.name);
                              if (res.success) {
                                messageApi.success('Selection deleted');
                                const r = await settingsService.getTargetSelections();
                                if (r.success) setSavedSelections(r.data || []);
                              } else messageApi.error(res.message || 'Failed to delete');
                            }}
                          />
                        </span>
                      </span>
                    )
                  }))}
                  notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No saved selections" />}
                />
                {!isReadOnly && selectedTargets.length > 0 && (
                  <>
                    <Input
                      size="small"
                      placeholder="Selection name..."
                      value={savedSelectionName}
                      onChange={e => setSavedSelectionName(e.target.value)}
                      style={{ width: 160 }}
                      onPressEnter={async () => {
                        const name = savedSelectionName.trim();
                        if (!name) return;
                        const existing = savedSelections.find(s => s.name === name);
                        const fn = existing
                          ? () => settingsService.updateTargetSelection(name, name, formData.technicalPaths || [], formData.displayPaths || [])
                          : () => settingsService.addTargetSelection(name, formData.technicalPaths || [], formData.displayPaths || []);
                        const res = await fn();
                        if (res.success) {
                          messageApi.success(existing ? 'Selection updated' : 'Selection saved');
                          const r = await settingsService.getTargetSelections();
                          if (r.success) setSavedSelections(r.data || []);
                        } else messageApi.error(res.message || 'Failed to save');
                      }}
                    />
                    <Button
                      size="small"
                      type="primary"
                      icon={<SaveOutlined />}
                      disabled={!savedSelectionName.trim()}
                      style={{ background: '#2E86AB', borderColor: '#2E86AB' }}
                      onClick={async () => {
                        const name = savedSelectionName.trim();
                        if (!name) return;
                        const existing = savedSelections.find(s => s.name === name);
                        const fn = existing
                          ? () => settingsService.updateTargetSelection(name, name, formData.technicalPaths || [], formData.displayPaths || [])
                          : () => settingsService.addTargetSelection(name, formData.technicalPaths || [], formData.displayPaths || []);
                        const res = await fn();
                        if (res.success) {
                          messageApi.success(existing ? 'Selection updated' : 'Selection saved');
                          const r = await settingsService.getTargetSelections();
                          if (r.success) setSavedSelections(r.data || []);
                        } else messageApi.error(res.message || 'Failed to save');
                      }}
                    >
                      Save
                    </Button>
                  </>
                )}
              </div>

              {/* Advanced Targeting Modal — removed */}
              {false && <Modal
                title="Advanced Targeting"
                open={advTargetVisible}
                onCancel={() => setAdvTargetVisible(false)}
                onOk={handleAdvancedTargetApply}
                okText="Add to Targets"
                okButtonProps={{ disabled: !advHasCriteria }}
                width={620}
              >
                {/* ── Saved Filters ── */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Select
                      placeholder="Load a saved filter..."
                      style={{ flex: 1, minWidth: 180 }}
                      size="small"
                      allowClear
                      value={undefined}
                      onChange={(filterName) => {
                        const f = advSavedFilters.find(f => f.name === filterName);
                        if (f) {
                          setAdvTargetKeywords(f.keywords || []);
                          setAdvTargetInput('');
                          setAdvTargetMatchMode(f.matchMode || 'word');
                          setAdvTargetBUs(f.businessUnits || []);
                          setAdvTargetExcludedGroups(new Set());
                          setAdvTargetPreviewExpanded(false);
                          setAdvTargetIncludedDepts(new Set());
                          setAdvSaveFilterName(f.name);
                        }
                      }}
                      options={advSavedFilters.map(f => ({
                        label: (
                          <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{f.name}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ color: '#999', fontSize: 11 }}>
                                {[
                                  f.keywords?.length ? `${f.keywords.length} keyword${f.keywords.length > 1 ? 's' : ''}` : '',
                                  f.matchMode && f.matchMode !== 'word' ? f.matchMode : '',
                                  f.businessUnits?.length ? `${f.businessUnits.length} BU${f.businessUnits.length > 1 ? 's' : ''}` : ''
                                ].filter(Boolean).join(' · ')}
                              </span>
                              <CloseOutlined
                                style={{ color: '#ff4d4f', fontSize: 10 }}
                                onClick={async e => {
                                  e.stopPropagation();
                                  const res = await settingsService.removeTargetingFilter(f.name);
                                  if (res.success) {
                                    messageApi.success('Filter deleted');
                                    const r = await settingsService.getTargetingFilters();
                                    if (r.success) setAdvSavedFilters(r.data || []);
                                  } else {
                                    messageApi.error(res.message || 'Failed to delete filter');
                                  }
                                }}
                              />
                            </span>
                          </span>
                        ),
                        value: f.name
                      }))}
                      notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No saved filters" />}
                    />
                  </div>
                  {advHasCriteria && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <Input
                        size="small"
                        placeholder="Filter name"
                        value={advSaveFilterName}
                        onChange={e => setAdvSaveFilterName(e.target.value)}
                        onPressEnter={handleSaveFilter}
                        style={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        icon={<SaveOutlined />}
                        disabled={!advSaveFilterName.trim()}
                        onClick={handleSaveFilter}
                      >
                        Save Filter
                      </Button>
                    </div>
                  )}
                </div>

                {/* Reset all filters */}
                {(advTargetBUs.length > 0 || advTargetKeywords.length > 0 || advTargetInput.trim()) && (
                  <div style={{ textAlign: 'right', marginBottom: 8 }}>
                    <a
                      style={{ fontSize: 12 }}
                      onClick={() => {
                        setAdvTargetKeywords([]);
                        setAdvTargetInput('');
                        setAdvTargetMatchMode('word');
                        setAdvTargetBUs([]);
                        setAdvTargetExcludedGroups(new Set());
                        setAdvTargetPreviewExpanded(false);
                        setAdvTargetIncludedDepts(new Set());
                      }}
                    >
                      Reset all filters
                    </a>
                  </div>
                )}

                {/* ── Managing group filter ── */}
                {organizations.length > 0 && (
                  <div style={{ marginBottom: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                        Managing group
                      </Text>
                      {advTargetBUs.length > 0 && (
                        <a style={{ fontSize: 12 }} onClick={() => setAdvTargetBUs([])}>Clear</a>
                      )}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {organizations.map(group => {
                        const groupBUs = (group.children || []).map(bu => bu.title);
                        const hasSelectedBUs = groupBUs.some(bu => advTargetBUs.includes(bu));
                        const isExcluded = advTargetExcludedGroups.has(group.title);
                        const isChecked = hasSelectedBUs && !isExcluded;
                        return (
                          <Tag.CheckableTag
                            key={group.value}
                            checked={isChecked}
                            onChange={checked => {
                              if (checked) {
                                setAdvTargetBUs(prev => Array.from(new Set([...prev, ...groupBUs])));
                                setAdvTargetExcludedGroups(prev => { const u = new Set(prev); u.delete(group.title); return u; });
                              } else {
                                setAdvTargetBUs(prev => prev.filter(b => !groupBUs.includes(b)));
                                setAdvTargetExcludedGroups(prev => new Set([...prev, group.title]));
                              }
                            }}
                          >
                            {group.title}
                          </Tag.CheckableTag>
                        );
                      })}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      Click a group to select all its business units at once.
                    </Text>
                  </div>
                )}

                <Divider style={{ margin: '14px 0' }} />

                {/* ── Business unit filter ── */}
                {(() => {
                  const visibleBUs = (organizations || [])
                    .flatMap(g => (g.children || []).map(bu => ({ ...bu, groupTitle: g.title })));
                  if (!visibleBUs.length) return null;
                  return (
                    <div style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                          Business unit
                        </Text>
                        {advTargetBUs.length > 0 && (
                          <a style={{ fontSize: 12 }} onClick={() => setAdvTargetBUs([])}>Clear</a>
                        )}
                      </div>
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 140, overflowY: 'auto', paddingRight: 2 }}>
                        {visibleBUs.map(bu => (
                          <Tag.CheckableTag
                            key={bu.value}
                            checked={advTargetBUs.includes(bu.title)}
                            onChange={checked =>
                              setAdvTargetBUs(prev =>
                                checked ? [...prev, bu.title] : prev.filter(b => b !== bu.title)
                              )
                            }
                          >
                            {bu.title}
                          </Tag.CheckableTag>
                        ))}
                      </div>
                      {advTargetBUs.length === 0 && (
                        <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                          None selected — matches all business units
                        </Text>
                      )}
                    </div>
                  );
                })()}

                <Divider style={{ margin: '14px 0' }} />

                {/* ── Department keyword filter ── */}
                <div style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                      Department keyword
                    </Text>
                    {advTargetKeywords.length > 0 && (
                      <a style={{ fontSize: 12 }} onClick={() => { setAdvTargetKeywords([]); setAdvTargetInput(''); }}>Clear</a>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      padding: '4px 8px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                      cursor: 'text',
                      minHeight: 38,
                      alignItems: 'center',
                    }}
                    onClick={e => e.currentTarget.querySelector('input')?.focus()}
                  >
                    {advTargetKeywords.map(kw => (
                      <Tag
                        key={kw}
                        closable
                        onClose={() => setAdvTargetKeywords(prev => prev.filter(k => k !== kw))}
                        color="blue"
                        style={{ margin: 0 }}
                      >
                        {kw}
                      </Tag>
                    ))}
                    <input
                      value={advTargetInput}
                      autoComplete="off"
                      onChange={e => setAdvTargetInput(e.target.value)}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ',') && advTargetInput.trim()) {
                          e.preventDefault();
                          const kw = advTargetInput.trim().replace(/,$/, '');
                          if (kw && !advTargetKeywords.includes(kw)) {
                            setAdvTargetKeywords(prev => [...prev, kw]);
                          }
                          setAdvTargetInput('');
                        } else if (e.key === 'Backspace' && !advTargetInput && advTargetKeywords.length) {
                          setAdvTargetKeywords(prev => prev.slice(0, -1));
                        }
                      }}
                      placeholder={advTargetKeywords.length ? '' : 'e.g. Service, Sales — press Enter to add each'}
                      style={{ border: 'none', outline: 'none', flex: 1, minWidth: 160, fontSize: 14, padding: '2px 0' }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Match type:</Text>
                    <Radio.Group
                      size="small"
                      value={advTargetMatchMode}
                      onChange={e => setAdvTargetMatchMode(e.target.value)}
                      optionType="button"
                      buttonStyle="solid"
                    >
                      <Radio.Button value="contains">Contains</Radio.Button>
                      <Radio.Button value="word">Word match</Radio.Button>
                      <Radio.Button value="exact">Exact</Radio.Button>
                    </Radio.Group>
                    <Tooltip title={
                      advTargetMatchMode === 'contains'
                        ? 'Matches if the keyword appears anywhere in the department name. E.g. "IT" matches "IT", "IT - Dev", "Audit"'
                        : advTargetMatchMode === 'word'
                        ? 'Matches the keyword as a whole word. E.g. "IT" matches "IT", "IT - Dev" but NOT "Audit"'
                        : 'Matches only if the department name exactly equals the keyword. E.g. "IT" matches only "IT"'
                    }>
                      <QuestionCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
                    </Tooltip>
                  </div>
                </div>

                {/* ── Live preview ── */}
                <div style={{ marginTop: 16 }}>
                  {!advHasCriteria ? (
                    <Alert
                      type="info"
                      showIcon
                      message="Select at least one filter above to preview and add targets."
                    />
                  ) : advTargetPreview?.depts > 0 ? (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
                        <Text strong style={{ fontSize: 14 }}>
                          <span style={{ color: '#52c41a' }}>{advTargetPreview.depts}</span>{' '}
                          department{advTargetPreview.depts === 1 ? '' : 's'} across{' '}
                          <span style={{ color: '#52c41a' }}>{advTargetPreview.orgs}</span>{' '}
                          organisation{advTargetPreview.orgs === 1 ? '' : 's'} matched
                        </Text>
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          onClick={() => setAdvTargetPreviewExpanded(prev => !prev)}
                        >
                          {advTargetPreviewExpanded ? 'Hide' : 'Review & Select'}
                        </Button>
                      </div>
                      
                      {/* Preview list with checkboxes */}
                      {advTargetPreviewExpanded && (() => {
                        const deptInfoMap = new Map();
                        (organizations || []).forEach(group => {
                          (group.children || []).forEach(bu => {
                            (bu.children || []).forEach(dept => {
                              if (advMatchedValues.includes(dept.value)) {
                                deptInfoMap.set(dept.value, {
                                  name: dept.title,
                                  bu: bu.title,
                                  group: group.title
                                });
                              }
                            });
                          });
                        });
                        return (
                          <div style={{ marginTop: 12, border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, maxHeight: 300, overflowY: 'auto', background: '#fafafa' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: '#555', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>{advTargetIncludedDepts.size} of {advMatchedValues.length} selected</span>
                              <span>
                                <a style={{ marginRight: 8 }} onClick={() => setAdvTargetIncludedDepts(new Set(advMatchedValues))}>All</a>
                                <a onClick={() => setAdvTargetIncludedDepts(new Set())}>None</a>
                              </span>
                            </div>
                            {[...advMatchedValues].sort((a, b) => {
                              const nameA = deptInfoMap.get(a)?.name || a;
                              const nameB = deptInfoMap.get(b)?.name || b;
                              return nameA.localeCompare(nameB);
                            }).map(deptValue => {
                              const info = deptInfoMap.get(deptValue);
                              const isChecked = advTargetIncludedDepts.has(deptValue);
                              const isAlreadyAdded = selectedTargets.includes(deptValue);
                              const toggle = () => {
                                const updated = new Set(advTargetIncludedDepts);
                                if (updated.has(deptValue)) { updated.delete(deptValue); } else { updated.add(deptValue); }
                                setAdvTargetIncludedDepts(updated);
                              };
                              return (
                                <div key={deptValue} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                                  onClick={toggle}>
                                  <Checkbox checked={isChecked} onChange={toggle} style={{ marginTop: 2 }} />
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: '#333', display: 'flex', alignItems: 'center', gap: 6 }}>
                                      {info?.name || deptValue}
                                      {isAlreadyAdded && <Tag color="success" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>Added</Tag>}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{info?.group} / {info?.bu}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="No departments matched — try adjusting your filters."
                    />
                  )}
                </div>
              </Modal>}

              {organizations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                  <TeamOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <div style={{ marginTop: '16px' }}>
                    <Text type="secondary">No organisations available</Text>
                  </div>
                </div>
              ) : panelSearch.trim() ? (
                /* ── Search results (flat list) ── */
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6 }}>
                  {(() => {
                    const q = panelSearch.trim();
                    const qLower = q.toLowerCase();
                    const escaped = qLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const wordRe = new RegExp(`(^|\\s|-|[&/])${escaped}($|\\s|-|[&/])`, 'i');
                    const matchesDept = title => wordRe.test(title) || title.toLowerCase() === qLower;
                    const results = [];
                    (organizations || []).forEach(group => {
                      (group.children || []).forEach(bu => {
                        (bu.children || []).forEach(dept => {
                          if (matchesDept(dept.title)) results.push({ dept, bu, group });
                        });
                      });
                    });
                    results.sort((a, b) => a.dept.title.localeCompare(b.dept.title));
                    const unselected = results.filter(r => !selectedTargets.includes(r.dept.value));
                    return (
                      <>
                        {/* Toolbar */}
                        <div style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', borderRadius: '6px 6px 0 0' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {results.length} department{results.length !== 1 ? 's' : ''} found
                          </Text>
                          {!isReadOnly && unselected.length > 0 && (
                            <Button
                              size="small"
                              type="link"
                              style={{ padding: 0 }}
                              onClick={() => {
                                const toAdd = unselected.map(r => r.dept.value);
                                handleTargetChange(Array.from(new Set([...selectedTargets, ...toAdd])));
                              }}
                            >
                              Select all {results.length}
                            </Button>
                          )}
                        </div>
                        {/* Results list */}
                        {!results.length ? (
                          <Empty description={`No departments matching "${panelSearch}"`} style={{ padding: 40 }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                            {results.map(({ dept, bu, group }) => {
                              const isChecked = selectedTargets.includes(dept.value);
                              return (
                                <div
                                  key={dept.value}
                                  style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: isReadOnly ? 'default' : 'pointer', borderBottom: '1px solid #f5f5f5', background: isChecked ? '#f0faff' : 'transparent' }}
                                  onClick={() => {
                                    if (isReadOnly) return;
                                    handleTargetChange(isChecked ? selectedTargets.filter(d => d !== dept.value) : [...selectedTargets, dept.value]);
                                  }}
                                >
                                  <Checkbox checked={isChecked} disabled={isReadOnly} onChange={() => {}} onClick={e => e.stopPropagation()} />
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 500 }}>{dept.title}</div>
                                    <div style={{ fontSize: 11, color: '#999' }}>{group.title} / {bu.title}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                /* ── Three-column drill-down panel ── */
                <div style={{ display: 'flex', border: '1px solid #f0f0f0', borderRadius: 6, overflowX: 'auto', minHeight: 320 }}>

                  {/* Column 1: Managing Groups */}
                  <div style={{ minWidth: 160, borderRight: '1px solid #f0f0f0', overflowY: 'auto', maxHeight: 420 }}>
                    <div style={{ padding: '7px 12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      Managing Group
                    </div>
                    {/* All option */}
                    {(() => {
                      const totalSelected = organizations.flatMap(g => (g.children || []).flatMap(bu => bu.children || [])).filter(d => selectedTargets.includes(d.value)).length;
                      const isAllActive = panelActiveGroup === null;
                      return (
                        <div
                          onClick={() => { setPanelActiveGroup(null); setPanelActiveBU(null); }}
                          style={{ padding: '10px 12px', cursor: 'pointer', background: isAllActive ? '#cce8f2' : 'transparent', borderLeft: `3px solid ${isAllActive ? '#267a96' : 'transparent'}`, display: 'flex', alignItems: 'center', gap: 6, userSelect: 'none' }}
                        >
                          <FolderOutlined style={{ fontSize: 13, color: totalSelected > 0 ? '#267a96' : '#bbb', flexShrink: 0 }} />
                          <span style={{ fontSize: 13, flex: 1, fontWeight: isAllActive ? 600 : 400 }}>All</span>
                          {totalSelected > 0 && (
                            <span style={{ fontSize: 11, color: '#fff', background: '#267a96', borderRadius: 10, padding: '1px 7px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {totalSelected}
                            </span>
                          )}
                          <RightOutlined style={{ fontSize: 9, color: isAllActive ? '#267a96' : '#ccc', flexShrink: 0 }} />
                        </div>
                      );
                    })()}
                    {organizations.map(group => {
                      const allDepts = (group.children || []).flatMap(bu => bu.children || []);
                      const selectedCount = allDepts.filter(d => selectedTargets.includes(d.value)).length;
                      const isActive = panelActiveGroup === group.title;
                      return (
                        <div
                          key={group.value}
                          onClick={() => { setPanelActiveGroup(group.title); setPanelActiveBU(null); }}
                          style={{ padding: '10px 12px', cursor: 'pointer', background: isActive ? '#cce8f2' : 'transparent', borderLeft: `3px solid ${isActive ? '#267a96' : 'transparent'}`, display: 'flex', alignItems: 'center', gap: 6, userSelect: 'none' }}
                        >
                          <FolderOutlined style={{ fontSize: 13, color: selectedCount > 0 ? '#267a96' : '#bbb', flexShrink: 0 }} />
                          <span style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selectedCount > 0 ? 600 : 400 }}>
                            {group.title}
                          </span>
                          {selectedCount > 0 && (
                            <span style={{ fontSize: 11, color: '#fff', background: '#267a96', borderRadius: 10, padding: '1px 7px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {selectedCount}
                            </span>
                          )}
                          <RightOutlined style={{ fontSize: 9, color: isActive ? '#267a96' : '#ccc', flexShrink: 0 }} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Column 2: Organisations */}
                  <div style={{ minWidth: 210, borderRight: '1px solid #f0f0f0', overflowY: 'auto', maxHeight: 420 }}>
                    <div style={{ padding: '7px 12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      Organisation
                    </div>
                    {(() => {
                      const visibleBUs = (panelActiveGroup
                        ? (organizations.find(g => g.title === panelActiveGroup)?.children || []).map(bu => ({ bu, groupTitle: panelActiveGroup }))
                        : organizations.flatMap(g => (g.children || []).map(bu => ({ bu, groupTitle: g.title }))))
                        .sort((a, b) => a.bu.title.localeCompare(b.bu.title));
                      return visibleBUs.map(({ bu, groupTitle }) => {
                        const buDepts = bu.children || [];
                        const selectedCount = buDepts.filter(d => selectedTargets.includes(d.value)).length;
                        const isFullySelected = buDepts.length > 0 && selectedCount === buDepts.length;
                        const isPartial = selectedCount > 0 && !isFullySelected;
                        const isActiveBU = panelActiveBU === bu.key;
                        return (
                          <div
                            key={bu.value}
                            style={{ padding: '10px 12px', cursor: 'pointer', background: isActiveBU ? '#cce8f2' : 'transparent', borderLeft: `3px solid ${isActiveBU ? '#267a96' : 'transparent'}`, display: 'flex', alignItems: 'center', gap: 8, userSelect: 'none' }}
                            onClick={() => { setPanelActiveGroup(groupTitle); setPanelActiveBU(bu.key); }}
                          >
                            <Checkbox
                              checked={isFullySelected}
                              indeterminate={isPartial}
                              disabled={isReadOnly}
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                const toSet = new Set(selectedTargets);
                                buDepts.forEach(d => e.target.checked ? toSet.add(d.value) : toSet.delete(d.value));
                                handleTargetChange(Array.from(toSet));
                                setPanelActiveGroup(groupTitle);
                                setPanelActiveBU(bu.key);
                              }}
                            />
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{ fontSize: 13, fontWeight: selectedCount > 0 ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {bu.title}
                              </div>
                              <div style={{ fontSize: 11, color: selectedCount > 0 ? '#267a96' : '#999' }}>
                                {!panelActiveGroup && <span style={{ color: '#bbb' }}>{groupTitle} · </span>}
                                {selectedCount}/{buDepts.length} dept{buDepts.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                            {selectedCount > 0 && (
                              <span style={{ fontSize: 11, color: '#fff', background: '#267a96', borderRadius: 10, padding: '1px 7px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {selectedCount}
                              </span>
                            )}
                            <RightOutlined style={{ fontSize: 9, color: isActiveBU ? '#267a96' : '#ccc', flexShrink: 0 }} />
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* Column 3: Departments */}
                  <div style={{ flex: 1, minWidth: 180, overflowY: 'auto', maxHeight: 420 }}>
                    <div style={{ padding: '7px 12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Department</span>
                      {!isReadOnly && panelActiveBU && (() => {
                        const activeGroup = organizations.find(g => g.title === panelActiveGroup);
                        const activeBU = activeGroup?.children?.find(bu => bu.key === panelActiveBU);
                        const depts = activeBU?.children || [];
                        const allChecked = depts.length > 0 && depts.every(d => selectedTargets.includes(d.value));
                        const noneChecked = depts.every(d => !selectedTargets.includes(d.value));
                        return (
                          <span style={{ display: 'flex', gap: 6 }}>
                            {!allChecked && <a style={{ fontSize: 11 }} onClick={() => { const toSet = new Set(selectedTargets); depts.forEach(d => toSet.add(d.value)); handleTargetChange(Array.from(toSet)); }}>All</a>}
                            {!noneChecked && <a style={{ fontSize: 11, color: '#ff4d4f' }} onClick={() => { const toRemove = new Set(depts.map(d => d.value)); handleTargetChange(selectedTargets.filter(d => !toRemove.has(d))); }}>None</a>}
                          </span>
                        );
                      })()}
                    </div>
                    {panelActiveBU ? (() => {
                      const activeGroup = organizations.find(g => g.title === panelActiveGroup);
                      const activeBU = activeGroup?.children?.find(bu => bu.key === panelActiveBU);
                      const depts = [...(activeBU?.children || [])].sort((a, b) => a.title.localeCompare(b.title));
                      if (!depts.length) return (
                        <div style={{ padding: '40px 16px', textAlign: 'center', color: '#bbb', fontSize: 13 }}>No departments</div>
                      );
                      return depts.map(dept => {
                        const isChecked = selectedTargets.includes(dept.value);
                        return (
                          <div
                            key={dept.value}
                            style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f5f5f5', cursor: isReadOnly ? 'default' : 'pointer', background: isChecked ? '#cce8f2' : 'transparent', userSelect: 'none' }}
                            onClick={() => {
                              if (isReadOnly) return;
                              handleTargetChange(isChecked ? selectedTargets.filter(d => d !== dept.value) : [...selectedTargets, dept.value]);
                            }}
                          >
                            <Checkbox checked={isChecked} disabled={isReadOnly} onChange={() => {}} onClick={e => e.stopPropagation()} />
                            <span style={{ fontSize: 13, color: isChecked ? '#267a96' : '#595959', fontWeight: isChecked ? 600 : 400 }}>{dept.title}</span>
                          </div>
                        );
                      });
                    })() : (
                      <div style={{ padding: '40px 16px', textAlign: 'center', color: '#bbb', fontSize: 13 }}>
                        Select an organisation
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedTargets.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  {renderInteractiveTargetManager()}
                </div>
              )}
              {selectedTargets.length === 0 && !panelSearch.trim() && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#7a5c00' }}>
                  Tip: search a department type (e.g. "Sales") and click <strong>Select all</strong> to add it across every organisation at once, then use the manager below to exclude specific business units.
                </div>
              )}
            </Card>
          </div>
        );

      case 3:
        return (
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <Title level={4} style={{ marginBottom: '24px' }}>Add Additional People</Title>
            
            <Card>
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary">
                  Search and add specific people to receive this campaign invitation, beyond the target departments.
                </Text>
              </div>

              {/* Saved presets bar — a preset captures both the department targets and this people list */}
              <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#e8f4fb', border: '1px solid #91c8e0', borderRadius: 8, padding: '8px 12px' }}>
                <Select
                  placeholder="Load saved preset..."
                  size="small"
                  style={{ minWidth: 200, flex: 1 }}
                  allowClear
                  value={undefined}
                  onFocus={() => settingsService.getTargetSelections().then(r => { if (r.success) setSavedSelections(r.data || []); })}
                  onChange={selName => {
                    const sel = savedSelections.find(s => s.name === selName);
                    if (!sel) return;
                    const converted = convertTechnicalPathsToTreeSelectValues(sel.technicalPaths || [], organizations);
                    handleTargetChange(converted);
                    setSelectedPeople((sel.people || []).map(p => ({
                      _id: p.user,
                      name: p.name,
                      surname: p.surname,
                      department: p.department,
                      businessUnit: p.businessUnit,
                      includeInResults: p.includeInResults !== false
                    })));
                    setSavedSelectionName(sel.name);
                    messageApi.success(`Loaded "${sel.name}" (${sel.technicalPaths?.length || 0} dept, ${sel.people?.length || 0} people)`);
                  }}
                  options={savedSelections.map(s => ({
                    value: s.name,
                    label: `${s.name} — ${s.technicalPaths?.length || 0} dept, ${s.people?.length || 0} people`
                  }))}
                  notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No saved presets" />}
                />
                {!isReadOnly && (selectedTargets.length > 0 || selectedPeople.length > 0) && (
                  <>
                    <Input
                      size="small"
                      placeholder="Preset name..."
                      value={savedSelectionName}
                      onChange={e => setSavedSelectionName(e.target.value)}
                      style={{ width: 160 }}
                    />
                    <Button
                      size="small"
                      type="primary"
                      icon={<SaveOutlined />}
                      disabled={!savedSelectionName.trim()}
                      style={{ background: '#2E86AB', borderColor: '#2E86AB' }}
                      onClick={async () => {
                        const name = savedSelectionName.trim();
                        if (!name) return;
                        const peoplePayload = selectedPeople.map(p => ({
                          user: p._id,
                          name: p.name,
                          surname: p.surname,
                          department: p.department,
                          businessUnit: p.businessUnit,
                          includeInResults: p.includeInResults !== false
                        }));
                        const existing = savedSelections.find(s => s.name === name);
                        const fn = existing
                          ? () => settingsService.updateTargetSelection(name, name, formData.technicalPaths || [], formData.displayPaths || [], peoplePayload)
                          : () => settingsService.addTargetSelection(name, formData.technicalPaths || [], formData.displayPaths || [], peoplePayload);
                        const res = await fn();
                        if (res.success) {
                          messageApi.success(existing ? 'Preset updated' : 'Preset saved');
                          const r = await settingsService.getTargetSelections();
                          if (r.success) setSavedSelections(r.data || []);
                        } else messageApi.error(res.message || 'Failed to save preset');
                      }}
                    >
                      Save as Preset
                    </Button>
                  </>
                )}
              </div>

              {/* Bulk-add people via CSV, matched against existing records by phone number */}
              <CsvPeopleImport
                businessUnitOptions={businessUnitOptions}
                onResolved={handleCsvPeopleResolved}
              />

              {/* Search Input */}
              <Search
                placeholder="Search by name, email, or phone number"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
                loading={searchLoading}
                enterButton={<SearchOutlined />}
                size="large"
                style={{ marginBottom: '16px' }}
                disabled={isReadOnly}
              />

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div style={{ 
                  marginBottom: '24px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px'
                }}>
                  <List
                    dataSource={searchResults}
                    renderItem={(user) => (
                      <List.Item
                        style={{ padding: '12px 16px', cursor: 'pointer' }}
                        onClick={() => handleAddPerson(user)}
                        actions={[
                          <Button 
                            type="link" 
                            icon={<UserAddOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddPerson(user);
                            }}
                          >
                            Add
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={`${user.name || ''} ${user.surname || ''}`.trim() || user.email}
                          description={
                            <Space direction="vertical" size={0}>
                              {user.email && (
                                <Text type="secondary">
                                  <MailOutlined /> {user.email}
                                </Text>
                              )}
                              {user.phoneNumber && (
                                <Text type="secondary">
                                  <PhoneOutlined /> {user.phoneNumber}
                                </Text>
                              )}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )}

              {/* Selected People */}
              {selectedPeople.length > 0 ? (
                <div style={{ 
                  marginTop: '24px',
                  padding: '16px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9'
                }}>
                  <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong style={{ fontSize: '14px' }}>
                      Selected People ({selectedPeople.length}):
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Toggle "Include in Results" to exclude from campaign statistics
                    </Text>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedPeople.map((person) => (
                      <div 
                        key={person._id}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <UserOutlined />
                          <span>
                            <div>{`${person.name || ''} ${person.surname || ''}`.trim() || person.email}</div>
                            {(person.department || person.businessUnit) && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {[person.department, person.businessUnit].filter(Boolean).join(' · ')}
                              </Text>
                            )}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Tooltip title={person.includeInResults !== false ? 'Included in campaign results' : 'Excluded from campaign results'}>
                            <Switch 
                              size="small"
                              checked={person.includeInResults !== false}
                              onChange={() => handleTogglePersonIncludeInResults(person._id)}
                              disabled={isReadOnly}
                              checkedChildren="Include"
                              unCheckedChildren="Exclude"
                            />
                          </Tooltip>
                          {!isReadOnly && (
                            <Button 
                              type="text" 
                              size="small" 
                              danger
                              onClick={() => handleRemovePerson(person._id)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Empty
                  description="No additional people selected. You can skip this step or search to add people."
                  style={{ marginTop: '24px' }}
                />
              )}
            </Card>
          </div>
        );

      case 4:
        console.log('Rendering review step (case 4) with formData:', formData);
        console.log('formData.targets in case 4:', formData.targets);
        
        // Prepare campaign details items
        const campaignItems = [
          {
            key: '1',
            label: 'Campaign Name',
            children: formData.name || 'Not specified',
          },
          {
            key: '2',
            label: 'Description',
            children: formData.description || 'No description provided',
          },
          {
            key: '3',
            label: 'Campaign Period',
            children: formData.startDate && formData.endDate
              ? `${dayjs(formData.startDate).format('MM/DD/YYYY HH:mm')} - ${dayjs(formData.endDate).format('MM/DD/YYYY HH:mm')}`
              : 'Campaign period not set',
          },
          {
            key: '4',
            label: 'Leaderboard',
            children: (() => {
              const typeLabels = {
                'none': { text: 'Disabled', color: undefined },
                'presentation': { text: 'Percentage Average', color: 'green' },
                'points-percentage': { text: 'Points & Percentage', color: 'blue' },
                'campaign-breakdown': { text: 'Per Campaign Breakdown', color: 'purple' }
              };
              const type = formData.settings.leaderboardType || 'none';
              const config = typeLabels[type] || typeLabels['none'];
              return <Tag color={config.color}>{config.text}</Tag>;
            })(),
          },
        ];

        // Prepare questionnaire items
        const questionnaireItems = selectedQuestionnaireDetails ? [
          {
            key: '1',
            label: 'Quiz Title',
            children: selectedQuestionnaireDetails.title,
          },
          {
            key: '2',
            label: 'Description',
            children: selectedQuestionnaireDetails.description || 'No description',
          },
          {
            key: '3',
            label: 'Questions',
            children: `${getSelectedQuestionCount()} questions`,
          },
          {
            key: '4',
            label: 'Estimated Duration',
            children: selectedQuestionnaireDetails.estimatedDuration ? 
              `${selectedQuestionnaireDetails.estimatedDuration} minutes` : 'Not specified',
          },
          {
            key: '5',
            label: 'Created By',
            children: selectedQuestionnaireDetails.createdBy?.name || 'Unknown',
          },
          {
            key: '6',
            label: 'Last Updated',
            children: selectedQuestionnaireDetails.updatedAt ? 
              new Date(selectedQuestionnaireDetails.updatedAt).toLocaleDateString() :
              new Date(selectedQuestionnaireDetails.createdAt).toLocaleDateString(),
          },
        ] : [];

        return (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Title level={4} style={{ marginBottom: '32px' }}>Review Campaign</Title>
            
            {/* Campaign Details */}
            <Card style={{ marginBottom: '24px' }}>
              <Descriptions 
                title="Campaign Details" 
                items={campaignItems} 
                column={1}
              />
            </Card>

            {/* Quiz Details */}
            {selectedQuestionnaireDetails && (
              <Card style={{ marginBottom: '24px' }}>
                <Descriptions 
                  title="Quiz Details" 
                  items={questionnaireItems} 
                  column={1}
                />
              </Card>
            )}

            {/* Target Details */}
            <Card style={{ marginBottom: '24px' }}>
              <Title level={5} style={{ marginBottom: '16px' }}>Target Selection</Title>
              {renderTargetSummary()}
            </Card>

            {/* Additional People */}
            {selectedPeople.length > 0 && (
              <Card>
                <Title level={5} style={{ marginBottom: '16px' }}>
                  Additional People ({selectedPeople.length})
                </Title>
                <Space wrap>
                  {selectedPeople.map((person) => (
                    <Tag
                      key={person._id}
                      style={{ 
                        padding: '4px 8px',
                        fontSize: '14px',
                        borderRadius: '4px'
                      }}
                    >
                      <UserOutlined /> {`${person.name || ''} ${person.surname || ''}`.trim() || person.email}
                    </Tag>
                  ))}
                </Space>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title={isEdit ? 'Edit Campaign' : 'New Campaign'}
        breadcrumb={isEdit ? BREADCRUMB_PATTERNS.editCampaign(formData.name || 'Campaign') : BREADCRUMB_PATTERNS.newCampaign}
      />

      {/* Presence banner */}
      {otherEditors.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            otherEditors.length === 1
              ? `${otherEditors[0].name} is also editing this campaign — saving may overwrite their changes.`
              : `${otherEditors.map(e => e.name).join(' and ')} are also editing this campaign.`
          }
        />
      )}

      <Card style={{ marginBottom: '24px' }}>
        {(formData.status === 'active' || formData.status === 'completed') && (
          <Alert
            message={`Campaign is ${formData.status === 'active' ? 'Active' : 'Completed'}`}
            description={`This campaign is currently ${formData.status} and cannot be modified. You can view the details but cannot make changes.`}
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}
        
        <Steps 
          current={currentStep} 
          style={{ marginBottom: '32px' }}
          onChange={handleStepClick}
          type="navigation"
        >
          {steps.map((step, index) => (
            <Step 
              key={index}
              title={step.title} 
              icon={step.icon}
              style={{ 
                cursor: isReadOnly ? 'not-allowed' : 'pointer'
              }}
            />
          ))}
        </Steps>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {renderStepContent()}

          <div style={{
            marginTop: '32px',
            textAlign: 'center',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '24px'
          }}>
            {isEdit && autoSaveStatus !== 'idle' && (
              <div style={{ marginBottom: 12, fontSize: 12, color: autoSaveStatus === 'saved' ? '#52c41a' : autoSaveStatus === 'saving' ? '#1890ff' : '#ff4d4f' }}>
                {autoSaveStatus === 'saving' && 'Auto-saving…'}
                {autoSaveStatus === 'saved' && 'Changes auto-saved'}
                {autoSaveStatus === 'error' && 'Auto-save failed'}
              </div>
            )}
            <Space>
              {currentStep > 0 && (
                <Button onClick={handlePrev} disabled={isReadOnly}>
                  Previous
                </Button>
              )}
              
              {currentStep < steps.length - 1 ? (
                <Button type="primary" onClick={handleNext} disabled={isReadOnly}>
                  Next
                </Button>
              ) : (
                <Button 
                  type="primary" 
                  onClick={handleSubmit}
                  loading={submitLoading}
                  icon={<CheckCircleOutlined />}
                  disabled={formData.status === 'active' || formData.status === 'completed'}
                >
                  {isEdit ? 'Update' : 'Create'} Campaign
                </Button>
              )}

              {/* Launch Campaign button - only show on review step for existing campaigns */}
              {currentStep === steps.length - 1 && isEdit && (
                <>
                  {formData.status === 'active' ? (
                    // Campaign is already launched
                    <Button 
                      type="default"
                      disabled
                      icon={<CheckCircleOutlined />}
                      style={{ 
                        backgroundColor: '#f0f0f0', 
                        borderColor: '#d9d9d9', 
                        color: '#999' 
                      }}
                    >
                      Campaign Active
                    </Button>
                  ) : formData.status === 'completed' ? (
                    // Campaign is completed
                    <Button 
                      type="default"
                      disabled
                      icon={<CheckCircleOutlined />}
                      style={{ 
                        backgroundColor: '#f0f0f0', 
                        borderColor: '#d9d9d9', 
                        color: '#999' 
                      }}
                    >
                      Campaign Completed
                    </Button>
                  ) : (
                    // Campaign can be launched (only for existing campaigns)
                    <Button 
                      type="default"
                      onClick={() => setShowLaunchModal(true)}
                      icon={<PlayCircleOutlined />}
                      style={{ 
                        backgroundColor: '#52c41a', 
                        borderColor: '#52c41a', 
                        color: 'white' 
                      }}
                    >
                      Launch Campaign
                    </Button>
                  )}
                </>
              )}
              
              <Button onClick={() => navigate('/dashboard/campaigns')}>
                Cancel
              </Button>
            </Space>
          </div>
        </Form>
      </Card>

      {/* Conflict modal: someone else saved while you were editing */}
      <Modal
        open={!!conflictInfo}
        title="Campaign edited by someone else"
        onCancel={() => setConflictInfo(null)}
        footer={null}
        width={480}
      >
        {conflictInfo && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="error"
              showIcon
              message={
                conflictInfo.savedByName
                  ? `${conflictInfo.savedByName} saved this campaign after you opened it.`
                  : 'This campaign was saved by someone else after you opened it.'
              }
            />
            <Text type="secondary">
              Your unsaved changes may conflict with their version. You can overwrite their changes with yours, or discard yours and reload the latest version.
            </Text>
            <Space>
              <Button
                danger
                onClick={async () => {
                  const payload = { ...conflictInfo.pendingData, autoSave: false };
                  const response = await campaignService.update(id, payload);
                  if (response.success) {
                    if (response.data?.updatedAt) serverUpdatedAtRef.current = response.data.updatedAt;
                    lastSavedJsonRef.current = JSON.stringify(conflictInfo.pendingData);
                    setAutoSaveStatus('saved');
                    messageApi.success('Campaign saved (overwrote other changes)');
                  } else {
                    messageApi.error('Failed to save');
                  }
                  setConflictInfo(null);
                }}
              >
                Overwrite their changes
              </Button>
              <Button
                onClick={async () => {
                  setConflictInfo(null);
                  window.location.reload();
                }}
              >
                Discard mine &amp; reload
              </Button>
            </Space>
          </Space>
        )}
      </Modal>

      <Modal
        title="Quiz already allocated"
        open={reuseModalState.open}
        onOk={handleReuseModalConfirm}
        onCancel={handleReuseModalCancel}
        okText="Use anyway"
        cancelText="Choose another"
      >
        <div style={{ marginBottom: 12 }}>
          <Text>
            This quiz is already part of other campaigns. Do you still want to reuse it here?
          </Text>
        </div>
        {renderQuestionnaireAllocationSummary(reuseModalState.summary, { limit: 5 })}
      </Modal>

      {/* Launch Campaign Confirmation Modal */}
      <Modal
        title="Launch Campaign"
        open={showLaunchModal}
        onCancel={() => setShowLaunchModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowLaunchModal(false)}>
            Cancel
          </Button>,
          <Button
            key="launch"
            type="primary"
            loading={launchLoading}
            icon={<PlayCircleOutlined />}
            onClick={handleLaunchCampaign}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Yes, Start Campaign
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '24px', textAlign: 'center' }}>
            <PlayCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={4}>Start This Campaign?</Title>
            <Text type="secondary">This will prepare everything. Participants will be notified when the scheduled start time arrives.</Text>
          </div>
          
          <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '12px' }}>
              <Text strong style={{ fontSize: '16px' }}>{formData.name}</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Text><strong>Survey:</strong> {selectedQuestionnaireDetails?.title}</Text>
              <Text><strong>Going to:</strong> {formData.displayPaths?.length || 0} departments</Text>
              <Text><strong>Takes about:</strong> {selectedQuestionnaireDetails?.estimatedDuration || '5'} minutes</Text>
            </div>
          </div>

          <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
            <Text>Ready to go? This will set up the campaign and notify people when it's time to start.</Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CampaignForm;